import { connectAppButtonClassName } from "@app/components/actions/blocked/ConnectAppCard";
import { getDefaultRemoteMCPServerByName } from "@app/lib/actions/mcp_internal_actions/remote_servers";
import {
  useGranolaApiKeyActions,
  useGranolaConnection,
} from "@app/lib/swr/granola";
import type { LightWorkspaceType } from "@app/types/user";
import { Button, CheckCircle, Icon, Input } from "@dust-tt/sparkle";
import { useState } from "react";

interface GranolaApiKeyRowProps {
  owner: LightWorkspaceType;
  // Either the Granola preset about to be installed, or an installed server.
  url?: string;
  mcpServerId?: string;
}

// Granola takes a second, optional credential. Sign-in is what lets agents read
// notes; the API key only lets Granola push a note the moment it is written.
// The key is the user's own, not the server's, so it can be saved before or
// after the sign-in and never holds up the turn.
export function GranolaApiKeyRow({
  owner,
  url,
  mcpServerId,
}: GranolaApiKeyRowProps) {
  const isGranolaPreset =
    !!url && url === getDefaultRemoteMCPServerByName("Granola")?.url;
  const { granolaConnection } = useGranolaConnection({
    owner,
    disabled: !isGranolaPreset && !mcpServerId,
  });
  const { saveApiKey } = useGranolaApiKeyActions({ owner });

  const [apiKey, setApiKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const isGranola =
    isGranolaPreset ||
    (!!mcpServerId && mcpServerId === granolaConnection?.mcpServerId);
  if (!isGranola || (granolaConnection?.hasApiKey && !isSaved)) {
    return null;
  }

  const onSave = async () => {
    setIsSaving(true);
    const res = await saveApiKey(apiKey.trim());
    setIsSaving(false);
    if (res.isOk()) {
      setIsSaved(true);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground dark:text-muted-foreground-night">
        <span className="h-px flex-1 bg-border dark:bg-border-night" />
        and for instant notes
        <span className="h-px flex-1 bg-border dark:bg-border-night" />
      </div>
      {isSaved ? (
        <div className="flex items-center gap-2 text-sm text-foreground dark:text-foreground-night">
          <Icon
            visual={CheckCircle}
            size="sm"
            className="text-success-500 dark:text-success-400"
          />
          API key added. New notes reach Memoair as soon as Granola writes them.
        </div>
      ) : (
        <>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-foreground dark:text-foreground-night">
              Granola API key
            </span>
            <span className="text-sm text-muted-foreground dark:text-muted-foreground-night">
              New notes reach Memoair the moment they're written. Needs a
              Business or Enterprise plan.
            </span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <Input
              type="password"
              placeholder="grn_…"
              aria-label="Granola API key"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={isSaving}
              message="In Granola: Settings → Connectors → API keys."
              containerClassName="flex-1"
            />
            <Button
              variant="highlight"
              size="sm"
              label={isSaving ? "Checking…" : "Save key"}
              isLoading={isSaving}
              disabled={isSaving || apiKey.trim().length === 0}
              onClick={() => void onSave()}
              className={connectAppButtonClassName}
            />
          </div>
        </>
      )}
    </div>
  );
}
